import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { Collection } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { CollectionKindBadge } from './collection-kind-badge';
import { CollectionStatusBadge } from './collection-status-badge';
import { CollectionThumb } from './collection-thumb';

/**
 * Column model of the collections table; `meta` ties columns to the list's sorts and filters.
 * The table adds the row menu itself.
 */
export function useCollectionColumns(storeId: string) {
  const { t } = useTranslation('collections');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<Collection>>>(
    () => [
      {
        id: 'title',
        header: t('columns.collection'),
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <CollectionThumb mediaId={row.original.mediaId} />
            <div className="min-w-0">
              <Link
                to="/s/$storeId/collections/$collectionId"
                params={{ storeId, collectionId: row.original.id }}
                className="block truncate font-medium hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {row.original.title}
              </Link>
              <code className="text-muted-foreground block truncate text-xs">
                {row.original.handle}
              </code>
            </div>
          </div>
        ),
        meta: { label: t('columns.collection'), sortField: 'title', lockVisibility: true },
      },
      {
        id: 'kind',
        accessorKey: 'kind',
        header: t('columns.kind'),
        cell: ({ row }) => <CollectionKindBadge kind={row.original.kind} />,
        meta: { filterKey: 'kind', tour: 'collections.list.kind-badge' },
        size: 120,
      },
      {
        id: 'productCount',
        accessorKey: 'productCount',
        header: t('columns.products'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('columns.productCount', { count: row.original.productCount })}
          </span>
        ),
        size: 120,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <CollectionStatusBadge status={row.original.status} />,
        meta: { filterKey: 'status' },
        size: 110,
      },
      {
        id: 'sortOrder',
        accessorKey: 'sortOrder',
        header: t('columns.sortOrder'),
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {t(`sortOrder.${row.original.sortOrder}`)}
          </span>
        ),
        size: 150,
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.updatedAt)}>
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { sortField: 'updatedAt', filterKey: 'updatedAt' },
        size: 140,
      },
    ],
    [format, storeId, t],
  );
}
